import { z } from 'zod';

import { Environment, getDeploymentEnvironment } from './deployment-env';
import { SanitizedStringSchema } from './util';

/**
 * Default bearer token used for the connect-graphql `/api/internal` surface when
 * `DEPLOYMENT_ENV` is unset or `'local'` and `CRAFT_INTERNAL_API_BEARER_TOKEN` has
 * not been set explicitly. Lets a fresh clone run the dev server without first
 * editing `.env`. MUST stay in sync with the same constant in
 * `services/connect-graphql/src/environment.ts`.
 */
const LOCAL_DEV_INTERNAL_API_BEARER_TOKEN = 'local-dev-internal-api-bearer-token';

const InternalApiBearerTokens: Record<Environment, string | undefined> = {
  [Environment.Demo]: undefined,
  [Environment.Dev]: undefined,
  [Environment.Local]: LOCAL_DEV_INTERNAL_API_BEARER_TOKEN,
  [Environment.Prod]: undefined,
  [Environment.Test]: undefined,
};

/**
 * A schema for internal API bearer tokens that applies the local-dev fallback when appropriate.
 * Returns `undefined` in non-local environments with no env var so schema validation can fail
 * loudly.
 */
export const InternalApiBearerTokenSchema = SanitizedStringSchema.optional().transform(
  (value, ctx) => {
    if (typeof value === 'string') {
      if (value.length < 32) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          inclusive: true,
          message: 'Internal API bearer token must be at least 32 characters long.',
          minimum: 32,
          type: 'string',
        });
        return z.NEVER;
      }
      return value;
    }
    const deploymentEnv = getDeploymentEnvironment();
    return InternalApiBearerTokens[deploymentEnv];
  },
);
